"use client";

import type { MouseEvent } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  hrefForServiceCard,
  serviceCardDefs,
  type ServiceCardDef,
  type ServiceCardIconId,
} from "@/config/service-cards";
import { IconHub, IconSettingsRemote, IconTerminal } from "@/components/icons";
import { CapabilityArticleExpandable } from "@/components/services/CapabilityArticleExpandable";
import { ServicesDepthIntroExpandable } from "@/components/services/ServicesDepthIntroExpandable";
import { InPracticeIndexFlair } from "@/components/services/InPracticeIndexFlair";
import { cn } from "@/lib/utils";

const labelClass =
  "text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-semibold text-[var(--primary)]";

const serviceIcons: Record<ServiceCardIconId, typeof IconTerminal> = {
  terminal: IconTerminal,
  remote: IconSettingsRemote,
  hub: IconHub,
};

function scrollToCapSection(capId: string, reduceMotion: boolean) {
  const el = document.getElementById(capId);
  if (!el) return false;
  el.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
  if (typeof window !== "undefined") {
    const { pathname, search } = window.location;
    window.history.replaceState(null, "", `${pathname}${search}#${capId}`);
  }
  return true;
}

export function ServicesInPracticeSection() {
  const t = useTranslations("servicesHub");
  const tCards = useTranslations("serviceCards");
  const reduceMotion = useReducedMotion();
  const prefersReducedMotion = reduceMotion === true;

  function onCapPillClick(e: MouseEvent<HTMLAnchorElement>, capId: string) {
    if (prefersReducedMotion) return;
    if (scrollToCapSection(capId, prefersReducedMotion)) {
      e.preventDefault();
    }
  }

  return (
    <section
      id="in-practice"
      className="relative isolate overflow-hidden site-section-x scroll-mt-28"
      style={{ backgroundColor: "var(--surface-container-low)" }}
      aria-labelledby="services-depth-heading"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-[20%] left-1/2 h-[min(85%,52rem)] w-[min(125%,90rem)] -translate-x-1/2 opacity-50 [background:radial-gradient(ellipse_58%_42%_at_50%_18%,color-mix(in_srgb,var(--design-ambient-tint)_26%,transparent)_0%,transparent_68%)]" />
        <div
          className="absolute inset-0 opacity-[0.4] [background-image:linear-gradient(color-mix(in_srgb,var(--on-surface)_4.5%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_srgb,var(--on-surface)_4.5%,transparent)_1px,transparent_1px)] [background-size:52px_52px] [mask-image:radial-gradient(ellipse_92%_70%_at_50%_32%,black_18%,transparent_72%)] [-webkit-mask-image:radial-gradient(ellipse_92%_70%_at_50%_32%,black_18%,transparent_72%)]"
          aria-hidden
        />
      </div>

      <div className="relative z-[1] site-container pt-14 sm:pt-16 md:pt-20 pb-6 sm:pb-8">
        <motion.div
          initial={reduceMotion ? undefined : { opacity: 0, y: 18 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.45 }}
          transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
        >
          <p
            className={cn(labelClass, "text-[var(--on-surface-variant)]/80")}
            style={{ fontFamily: "var(--font-body)" }}
          >
            {t("inPractice")}
          </p>
          <h2
            id="services-depth-heading"
            className="mt-4 font-display text-[clamp(1.75rem,4.2vw,2.85rem)] text-[var(--on-surface)] max-w-[44rem] leading-[1.12] tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("depthHeading")}
          </h2>
          <ServicesDepthIntroExpandable className="mt-4 max-w-[40rem]" />
        </motion.div>

        <nav
          className="mt-6 flex flex-wrap gap-2 sm:gap-2.5"
          aria-label={t("depthNavAria")}
        >
          {serviceCardDefs.map((def) => {
            const capId = `cap-${def.iconId}`;
            const Icon = serviceIcons[def.iconId];
            const title = tCards(`${def.capability}.title`);
            return (
              <Link
                key={hrefForServiceCard(def)}
                href={`#${capId}`}
                scroll={false}
                onClick={(e) => onCapPillClick(e, capId)}
                className={cn(
                  "in-practice-cap-pill group inline-flex items-center gap-2.5 rounded-full border border-[color-mix(in_srgb,var(--outline-variant)_38%,transparent)]",
                  "bg-[var(--surface)] px-3.5 py-2 sm:px-4 sm:py-2.5",
                  "text-sm font-medium text-[var(--on-surface)] shadow-[0_14px_44px_-26px_color-mix(in_srgb,var(--primary)_38%,transparent)]",
                  "hover:border-[color-mix(in_srgb,var(--primary)_26%,transparent)] hover:shadow-[0_22px_52px_-22px_color-mix(in_srgb,var(--primary)_32%,transparent)]",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]",
                )}
                style={{ fontFamily: "var(--font-body)" }}
              >
                <span
                  className="in-practice-cap-pill-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--primary-container)_88%,transparent)] text-[var(--primary)] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_26%,transparent)]"
                  aria-hidden
                >
                  <span className="[&_svg]:h-[18px] [&_svg]:w-[18px]">
                    <Icon />
                  </span>
                </span>
                <span className="pr-0.5 tracking-tight">{title}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="relative z-[1] site-container">
        {serviceCardDefs.map((card: ServiceCardDef, index: number) => {
          const n = String(index + 1).padStart(2, "0");
          const flip = index % 2 === 1;
          const capId = `cap-${card.iconId}`;
          const motionDelay = index * 0.05;

          return (
            <article
              key={hrefForServiceCard(card)}
              id={capId}
              className="[content-visibility:auto] [contain-intrinsic-size:auto_720px] grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-start py-20 sm:py-24 md:py-28 first:pt-8 sm:first:pt-10 scroll-mt-28"
            >
              {flip ? (
                <>
                  <div className="lg:col-span-7 lg:col-start-1 order-2 lg:order-1 min-w-0">
                    <CapabilityArticleExpandable
                      indexLabel={n}
                      card={card}
                      articleIndex={index}
                    />
                  </div>
                  <div className="lg:col-span-4 lg:col-start-9 order-1 lg:order-2">
                    <InPracticeIndexFlair indexLabel={n} motionDelay={motionDelay} />
                  </div>
                </>
              ) : (
                <>
                  <div className="lg:col-span-4 lg:col-start-1">
                    <InPracticeIndexFlair indexLabel={n} motionDelay={motionDelay} />
                  </div>
                  <div className="lg:col-span-7 lg:col-start-6 min-w-0">
                    <CapabilityArticleExpandable
                      indexLabel={n}
                      card={card}
                      articleIndex={index}
                    />
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
